import React, { useState, useEffect, useContext, useMemo } from 'react'
import { CompanyContext } from 'contexts/CompanyContext'
import { api as Api } from 'api/http';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import FormDesription from './FormDesription'
import { Trans } from 'react-i18next'
import { Button, Checkbox, Notification, Tooltip, toast, Card } from 'components/ui'
import { LuSave } from 'react-icons/lu'
import { SessionContext } from 'contexts/SessionContext'
import { Loading } from 'components/shared'
import { PriorityModalContext } from 'contexts/PriorityModalContext'
import AIImageDropdownModels from 'views/ai_image/components/AIImageDropdownModels'
import { AIContext, aiSizeOptions } from 'contexts/AIContext'
import AIImageDropdownSizes from 'views/ai_image/components/AIImageDropdownSizes'
import { PLACEMENT } from 'components/ui/utils/constant'
import ModelCard from './ModelCard'
import AspectRatioCard from './AspectRatioCard'
import { 
    HiPhotograph,
    HiCollection,
    HiColorSwatch,
    HiInformationCircle,
    HiEye,
    HiSparkles
} from 'react-icons/hi'
import { BsStars, BsPalette } from 'react-icons/bs'
import { RiImageAiLine } from 'react-icons/ri'

export const aiImageStyles = [
    { style: `No style`, description: `No specific artistic or visual style is applied. The generated image will appear neutral and unembellished.`, image: `/img/others/noimage.jpg` },
    { style: `Realism`, description: `Depicts subjects with accuracy and detail, focusing on objective representation and lifelike appearance.`, image: `https://cdn.followr.ai/elephants/9a90de76-f7d2-4f24-98ed-11b6d991d3af.jpg` },
    { style: `Impressionism`, description: `Captures the mood of a scene using loose brush strokes and vivid colors, evoking atmosphere and feeling.`, image: `https://cdn.followr.ai/elephants/9a90deac-a6c3-4b93-afbf-fd4cc2cbab76.jpg` },
    { style: `Trompe l’oeil`, description: `Uses realistic imagery to create optical illusions, making objects appear three-dimensional and lifelike.`, image: `https://cdn.followr.ai/elephants/9a90da72-e80f-470c-a133-ff1fd9a92184.jpg` },
    { style: `Conceptual Art`, description: `Focuses on the idea behind the artwork, prioritizing concept and meaning over physical appearance.`, image: `https://cdn.followr.ai/elephants/9a90df35-fa29-4108-8c87-195ecb5ce438.jpg` },
    { style: `Minimalism`, description: `Emphasizes simplicity and geometric forms, using limited color and composition for a clean look.`, image: `https://cdn.followr.ai/elephants/9a90df85-29cf-4fdd-9d9c-d09a92df1ab5.jpg` },
    { style: `Clipart`, description: `Simple digital illustrations or icons, often used in graphic design projects for clear communication.`, image: `https://cdn.followr.ai/elephants/clipart.jpg` },
    { style: `Pencil Art`, description: `Images created with pencil strokes, offering a hand-drawn look that can be realistic or creative.`, image: `https://cdn.followr.ai/elephants/pencilart.jpg` },
    { style: `Hyperrealism`, description: `Achieves an extremely high level of realism, often surpassing photographs in detail and clarity.`, image: `https://cdn.followr.ai/elephants/9a90dfba-224c-4980-b731-c568b1afd645.jpg` },
    { style: `Constructivism`, description: `Abstract, geometric art influenced by early 20th-century Russian design, focusing on structure and form.`, image: `https://cdn.followr.ai/elephants/9a90dfdc-48ea-49dc-835e-8b51d7d20d31.jpg` },
    { style: `Cubism`, description: `Depicts subjects in fragmented, abstracted forms, exploring multiple perspectives in a single image.`, image: `https://cdn.followr.ai/elephants/9a90e0d6-bd15-4e8d-af8f-26b5d54a5237.jpg` },
    { style: `Dada`, description: `Embraces absurdity and randomness, challenging traditional art through playful and unconventional imagery.`, image: `https://cdn.followr.ai/elephants/9a90e0f9-dc94-44e7-b182-3b4617245247.jpg` },
    { style: `Futurism`, description: `Highlights speed, technology, and modernity, using dynamic lines and energetic compositions.`, image: `https://cdn.followr.ai/elephants/9a90e11f-1d16-44cb-8e07-4136674d7aa8.jpg` },
    { style: `Surrealism`, description: `Blends dreamlike and realistic elements, creating imaginative scenes that defy logic and expectation.`, image: `https://cdn.followr.ai/elephants/9a90e1b2-ba3d-443a-96a5-2cc587cbdd15.jpg` },
    { style: `Pop Art`, description: `Features imagery from popular culture and mass media, using bold colors and graphic styles.`, image: `https://cdn.followr.ai/elephants/9a90e1db-99a5-4d17-b64d-7ac19569d33e.jpg` },
    { style: `Art Deco`, description: `Showcases geometric shapes and decorative patterns, inspired by the 1920s and 1930s design trends.`, image: `https://cdn.followr.ai/elephants/9a90e206-1bb0-4927-bdc1-d9a1e974d6ba.jpg` },
    { style: `Expressionism`, description: `Conveys emotion and psychology through bold color, texture, and expressive brushwork.`, image: `https://cdn.followr.ai/elephants/9a90e22c-1e86-490d-bf5c-0a15531316e7.jpg` },
    { style: `Art Nouveau`, description: `Features flowing, organic forms and decorative lines, often inspired by natural elements.`, image: `https://cdn.followr.ai/elephants/9a90e2f1-5853-4a9a-987c-251402ebc040.jpg` },
    { style: `Fauvism`, description: `Uses bold, non-naturalistic colors and simplified forms to create vibrant, energetic images.`, image: `https://cdn.followr.ai/elephants/9a90e30a-3758-4d09-9e31-1ed52e4f46a4.jpg` },
    { style: `Abstract`, description: `Focuses on shapes, colors, and lines rather than realistic representation, creating non-objective art.`, image: `https://cdn.followr.ai/elephants/9a90e3ad-f295-4e94-9e79-b66c37905256.jpg` },
    { style: `3D Model`, description: `Digital objects or scenes created with depth and realism using computer software and rendering.`, image: `https://cdn.followr.ai/elephants/3dmodel.jpg` },
    { style: `Cinematic`, description: `Evokes the atmosphere of films, using dramatic lighting, composition, and a movie-like aesthetic.`, image: `https://cdn.followr.ai/elephants/cinematic.jpg` },
    { style: `Isometric`, description: `Depicts objects or scenes from a fixed angle, showing three dimensions without perspective distortion.`, image: `https://cdn.followr.ai/elephants/isometric.jpg` },
    { style: `Line Art`, description: `Composed solely of lines, often simple and minimalist, focusing on outlines and contours.`, image: `https://cdn.followr.ai/elephants/lineart.jpg` },
    { style: `Photographic`, description: `Replicates real-world scenes with detail and accuracy, as if captured by a camera lens.`, image: `https://cdn.followr.ai/elephants/photographic.jpg` },
    { style: `Digital Painting`, description: `Paintings created using digital tools, mimicking traditional painting techniques and textures.`, image: `https://cdn.followr.ai/elephants/9a90f54d-53bf-4773-ad53-e0f1f0e2e59a.jpg` },
    { style: `Digital Drawing`, description: `Drawings made with digital tools, offering clean lines and flexibility in style and detail.`, image: `https://cdn.followr.ai/elephants/9a90f4f8-1aff-4c8a-a58f-74f8b8b0392d.jpg` },
    { style: `Digital Art`, description: `Art created using digital technology, including computer-generated images and 3D models.`, image: `https://cdn.followr.ai/elephants/9a90f5d5-682e-4f97-aef1-e0690de9bd5e.jpg` },
    { style: `Fantasy`, description: `Features magical creatures, supernatural elements, and mythical settings in imaginative scenes.`, image: `https://cdn.followr.ai/elephants/9a90f761-4fe7-49de-84f3-2a184b00e90c.jpg` },
    { style: `Pixel Art`, description: `Digital art made from individual pixels, reminiscent of early video game graphics and retro style.`, image: `https://cdn.followr.ai/elephants/9a90f6f0-ee16-4acd-a826-fd5ecf28d528.jpg` },
    { style: `Cyberpunk`, description: `Futuristic style with dystopian settings, advanced technology, neon colors, and cybernetic themes.`, image: `https://cdn.followr.ai/elephants/9a90f80c-e793-4be4-9af3-9648f79dda63.jpg` },
    { style: `Vaporwave`, description: `Combines 80s and 90s nostalgia with surreal, dreamlike compositions and pastel color palettes.`, image: `https://cdn.followr.ai/elephants/9a90f880-60d5-4e5e-be61-f8d6f77ab600.jpg` },
    { style: `Comic Book`, description: `Bold lines, dynamic action, and stylized characters, inspired by American and European comics.`, image: `https://cdn.followr.ai/elephants/9a90f8be-63dc-4fd6-b031-e336106e67e9.jpg` },
    { style: `Low Poly`, description: `3D art style using a limited number of polygons to create simple, geometric, and stylized shapes.`, image: `https://cdn.followr.ai/elephants/9a90f978-8c4d-45cd-9c3d-e052940b9333.jpg` },
    { style: `Glitch Art`, description: `Incorporates digital errors, distortions, and noise to create visually striking, abstract images.`, image: `https://cdn.followr.ai/elephants/9a90f9cb-99d3-40f1-9195-21f32f8c1e00.jpg` },
    { style: `Manga`, description: `Japanese comic book style, often detailed and expressive, similar in appearance to anime.`, image: `https://cdn.followr.ai/elephants/9a90fa05-54ae-4561-93ca-5f826625a1da.jpg` },
    { style: `Anime`, description: `Japanese animation style with vibrant colors, expressive faces, and dynamic movement.`, image: `https://cdn.followr.ai/elephants/9a90fb03-7f96-44ff-bde4-ea5af289bc22.jpg` },
    { style: `Sci-Fi`, description: `Focuses on futuristic settings, advanced technology, and extraterrestrial life in imaginative scenes.`, image: `https://cdn.followr.ai/elephants/9a90fb6b-3cfe-401c-9186-f380df7f08ac.jpg` },
    { style: `Nature Photography`, description: `Depicts wildlife, plants, and natural environments, highlighting the beauty of the outdoors.`, image: `https://cdn.followr.ai/elephants/9a91092d-a2ce-44d8-b7a3-32a32cebd701.jpg` },
    { style: `Street Photography`, description: `Candid images of people and events in public spaces, capturing everyday urban life.`, image: `https://cdn.followr.ai/elephants/9a910c97-a69b-41d4-b34f-1fdd67165d2b.jpg` },
    { style: `Holga`, description: `Uses the Holga camera, known for light leaks, vignetting, and soft focus, creating dreamy photos.`, image: `https://cdn.followr.ai/elephants/9a910e39-d931-41fd-ba65-0fb5aaf9b480.jpg` },
    { style: `Double Exposure Photography`, description: `Combines two or more images into one frame, resulting in surreal and dreamlike compositions.`, image: `https://cdn.followr.ai/elephants/9a910ea7-f804-42ad-a480-6bf8251a864d.jpg` },
    { style: `Performance Photography`, description: `Captures live art performances and events, focusing on movement and dramatic moments.`, image: `https://cdn.followr.ai/elephants/9a911254-368a-4824-b90d-89b673043188.jpg` },
    { style: `Underwater Photography`, description: `Features underwater environments and marine life, revealing the beauty beneath the surface.`, image: `https://cdn.followr.ai/elephants/9a91129e-bd25-46f9-9979-6588fab81010.jpg` },
    { style: `Time-Lapse Photography`, description: `A sequence of images taken over time, showing gradual change or movement in a single video.`, image: `https://cdn.followr.ai/elephants/9a9112e3-f2f1-4045-9ac0-63238351d5dc.jpg` },
    { style: `Documentary Photography`, description: `Captures real-life events and people, aiming to tell stories and document reality authentically.`, image: `https://cdn.followr.ai/elephants/9a911379-5771-46a5-8513-3f25ad0610cb.jpg` },
    { style: `Photograms`, description: `Cameraless images made by placing objects on photosensitive paper and exposing them to light.`, image: `https://cdn.followr.ai/elephants/9a911548-bf89-4910-b08e-c9c929c4c0af.jpg` },
    { style: `New Objectivity`, description: `Focuses on unsentimental realism and social critique in painting and photography, often direct.`, image: `https://cdn.followr.ai/elephants/9a91158d-fc6c-4311-a0bb-8c39145581a6.jpg` },
    { style: `Installation Photography`, description: `Documents large-scale, immersive art installations, capturing their spatial and visual impact.`, image: `https://cdn.followr.ai/elephants/9a9115f4-c8c1-45b1-9569-e05d35664e8a.jpg` },
    { style: `Film Noir`, description: `Inspired by classic film noir, uses dark, moody lighting and dramatic shadows for atmosphere.`, image: `https://cdn.followr.ai/elephants/9a9116b1-8b20-4813-8bfa-4d2217c6599c.jpg` },
    { style: `Aerial Photography`, description: `Captures landscapes and subjects from above, often using drones for unique perspectives.`, image: `https://cdn.followr.ai/elephants/9a911737-fc5a-41d0-bb30-85d716e93fbf.jpg` },
    { style: `Micro Photography`, description: `Extremely close-up images, often using microscopes, to reveal details invisible to the naked eye.`, image: `https://cdn.followr.ai/elephants/9a9118f0-864f-4735-b581-0c24bf44908e.jpg` },
    { style: `Macro Photography`, description: `Close-up images of small subjects, revealing intricate details and textures not usually seen.`, image: `https://cdn.followr.ai/elephants/9a911931-f1a9-4aa7-a200-e2c03e2c4b22.jpg` },
    { style: `Portrait Photography`, description: `Focuses on capturing the likeness and personality of individuals or groups in expressive ways.`, image: `https://cdn.followr.ai/elephants/9a911b20-2db6-4c09-9ead-1a73558423ca.jpg` },
    { style: `Landscape Photography`, description: `Captures natural and man-made environments, highlighting scenery and the beauty of places.`, image: `https://cdn.followr.ai/elephants/9a911ba0-f7e5-43c1-ac19-dec53040551b.jpg` },
    { style: `Still Life Photography`, description: `Features arranged inanimate objects as the subject, focusing on composition and lighting.`, image: `https://cdn.followr.ai/elephants/9a911c22-57ff-410f-a02f-936a89ded0f1.jpg` },
    { style: `Seascape Photography`, description: `Depicts bodies of water, coastlines, and marine life, emphasizing the beauty of the sea.`, image: `https://cdn.followr.ai/elephants/9a911d98-e048-4661-914a-e25d52eeae8e.jpg` },
    { style: `Astrophotography`, description: `Captures celestial objects and astronomical events, revealing the wonders of the night sky.`, image: `https://cdn.followr.ai/elephants/9a928976-58c6-4794-a13a-1e6c0f440696.jpg` },
    { style: `Animated Photography`, description: `Combines photography with animation techniques, creating moving or dynamic visual effects.`, image: `https://cdn.followr.ai/elephants/9a92899d-5b92-44fd-bd0f-7386a90cf511.jpg` },
    { style: `Banksy Photography`, description: `Focuses on the work of street artist Banksy, documenting his iconic and provocative art.`, image: `https://cdn.followr.ai/elephants/9a9289d0-35e5-46a4-b59d-1dd505b11c62.jpg` },
    { style: `Futurist Photography`, description: `Captures the dynamism, speed, and energy of the Futurist art movement in photographic form.`, image: `https://cdn.followr.ai/elephants/9a928baa-4ff7-41bb-bd26-bbd8085d57c9.jpg` },
    { style: `Wildlife Photography`, description: `Captures animals in their natural habitats, highlighting behavior and the beauty of nature.`, image: `https://cdn.followr.ai/elephants/9a928a44-db76-4319-a772-356004006eff.jpg` },
    { style: `Cyanotypes`, description: `A photographic process that produces cyan-blue prints using sunlight and photosensitive chemicals.`, image: `https://cdn.followr.ai/elephants/9a928be4-4a47-44f1-9138-3a9665e7e78c.jpg` },
    { style: `Graffiti Photography`, description: `Features street art and graffiti, capturing vibrant murals and urban artistic expression.`, image: `https://cdn.followr.ai/elephants/9a91216a-c21f-44db-9ab7-1d0f22010de5.jpg` },
    { style: `Cityscape Photography`, description: `Depicts urban environments and architecture, focusing on skylines and city life.`, image: `https://cdn.followr.ai/elephants/9a928c18-a6ff-4731-a88a-ee31a7ef1a35.jpg` },
    { style: `Mixed Media Photography`, description: `Combines photography with other artistic mediums, blending techniques for unique results.`, image: `https://cdn.followr.ai/elephants/9a928c42-0f6d-4945-8b07-dc6b6fbeaa0f.jpg` },
    { style: `Stop-Motion Photography`, description: `A series of photographs combined to create the illusion of movement, like animation frames.`, image: `https://cdn.followr.ai/elephants/9a928f14-a817-4043-80ea-1519c65b3535.jpg` },
    { style: `Lomography`, description: `Embraces imperfections and spontaneity using low-fidelity cameras for playful, colorful images.`, image: `https://cdn.followr.ai/elephants/9a928f7f-d20f-4837-b2af-c28c78150c1d.jpg` },
    { style: `Pinhole Photography`, description: `Uses a small hole in a light-tight container to project an image, creating soft, unique photos.`, image: `https://cdn.followr.ai/elephants/9a928fc5-55b8-41ee-a9f7-e55489419e71.jpg` },
];

const AIImages = ({ isModal = false }) => {

    const { getDriverModel, aiModels, aiImageModels } = useContext(AIContext);
    const { closeModalPriority } = useContext(PriorityModalContext);
    const { company, updateCompany } = useContext(CompanyContext);
    const { updateUserData } = useContext(SessionContext);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ selectedStyles, setSelectedStyles ] = useState([]);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const { model: imageModel, size: imageSize } = getDriverModel("image");
    const { model: videoModel, size: videoSize } = getDriverModel("video");

    // Get available models and current model info
    const availableModels = useMemo(() =>
        aiImageModels?.filter(m => !m.disabled && !m.hidden) || [],
        [aiImageModels]
    );
    
    const currentModel = useMemo(() => 
        availableModels.find(m => m.model === imageModel) || availableModels[0],
        [availableModels, imageModel]
    );

    // Get available size options for the current model
    const availableSizeOptions = useMemo(() => {
        if (!currentModel?.availableSizes) return [];
        
        return aiSizeOptions.filter(sizeOption => 
            currentModel.availableSizes.includes(sizeOption.id)
        );
    }, [currentModel, aiSizeOptions]);

    const refresh = async () => {
        setIsLoading(true);
        setSelectedStyles(company?.ai_image_styles || []);
        setIsLoading(false);
    }

    const handleOnClick = (style) => {
        if (!!selectedStyles.find(e => e == style)) {
            setSelectedStyles(selectedStyles.filter(e => e != style));
        } else {
            setSelectedStyles([...selectedStyles, style]);
        }
    }

    const handleOnSave = async () => {
        setIsSubmitting(true);
        try {
            await Api.put(`/companies/${company?.id}`, {}, { ai_image_styles: selectedStyles.length ? selectedStyles : null })
            updateUserData();
            isModal && closeModalPriority()
            toast.push(<Notification title="AI image styles saved successfully" type="success" />);
        } catch (err) {
            toast.push(<Notification title={err?.message || err} type="danger" />);
        }
        setIsSubmitting(false);
    }

    const updateCompanyImageModel = async (_model) => {
        const ai_preferences = {
            ...company?.ai_preferences,
            image_model: _model
        }
        await updateCompany({ ai_preferences });
    }

    const updateCompanyVideoModel = async (_model) => {
        const ai_preferences = {
            ...company?.ai_preferences,
            video_model: _model
        }
        await updateCompany({ ai_preferences });
    }

    const updateCompanyImageSize = async (_size) => {
        const ai_preferences = {
            ...company?.ai_preferences,
            image_aspect_ratio: _size
        }
        await updateCompany({ ai_preferences });
    }

    const updateCompanyVideoSize = async (_size) => {
        const ai_preferences = {
            ...company?.ai_preferences,
            video_aspect_ratio: _size
        }
        await updateCompany({ ai_preferences });
    }

    useEffect(() => {
        refresh();
    }, [company?.id]);

    useEffect(() => {
        if (!!imageModel && !aiModels?.find(m => m.model == imageModel)?.availableSizes?.includes(imageSize)) {
            updateCompanyImageSize(aiSizeOptions?.filter(e => aiModels?.find(m => m.model == imageModel)?.availableSizes?.includes(e.id))?.[0]?.id);
        }
    }, [imageModel]);

    return (
        <div className="w-full space-y-8">
            {isLoading && <Loading type='element' />}
            
            {/* Header Section */}
            <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg">
                        <HiPhotograph className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        AI Image Generation Settings
                    </h2>
                </div>
                <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                    Configure your AI image generation preferences. Choose from powerful models, customize aspect ratios, and select artistic styles to create stunning visuals for your content.
                </p>
            </div>

            {/* Current Configuration Summary */}
            <Card className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border-pink-200 dark:border-pink-700/50">
                <div className="p-0">
                    <div className="flex items-center gap-3 mb-4">
                        <BsStars className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Current Configuration
                        </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                            <img 
                                src={currentModel?.image} 
                                alt={currentModel?.name}
                                className="w-8 h-8 rounded"
                            />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    {currentModel?.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {currentModel?.price} credits per image
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                                <HiCollection className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                            </div>
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    {imageSize} Aspect Ratio
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {imageSize === '16:9' && 'Landscape format'}
                                    {imageSize === '1:1' && 'Square format'}
                                    {imageSize === '9:16' && 'Portrait format'}
                                    {!['16:9', '1:1', '9:16'].includes(imageSize) && 'Custom format'}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded flex items-center justify-center">
                                <BsPalette className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    {selectedStyles.length} Style{selectedStyles.length !== 1 ? 's' : ''}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {selectedStyles.length === 0 ? 'No styles selected' : 
                                     selectedStyles.length === aiImageStyles.length ? 'All styles selected' :
                                     'Custom selection'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* AI Image Model Selection */}
            <div className="space-y-6">
                {/* Quick Selector Dropdowns for Compact View */}
                <div className="lg:hidden space-y-4">
                    <AIImageDropdownModels 
                        model={imageModel}
                        setModel={updateCompanyImageModel}
                        className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                        parentClassName="w-full"
                        variant="twoTone"
                        placement={PLACEMENT.BOTTOM_START}
                    />
                </div>

                {/* Model Cards for Desktop */}
                <div className="hidden lg:block space-y-6">
                    {availableModels.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <RiImageAiLine className="w-5 h-5 text-pink-500" />
                                <h4 className="font-medium text-gray-900 dark:text-white">Image Models</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {availableModels.map(model => (
                                    <ModelCard
                                        key={model.model}
                                        model={model}
                                        isSelected={model.model === imageModel}
                                        onSelect={updateCompanyImageModel}
                                        tier="premium"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Aspect Ratio Selection */}
            <div className="space-y-6">
                <FormDesription
                    title={
                        <span className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <HiEye className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                            Image Aspect Ratio
                        </span>
                    }
                    desc={
                        <span className="text-gray-600 dark:text-gray-300">
                            Choose the aspect ratio for your generated images. Different ratios work better for different social media platforms.
                        </span>
                    }
                />

                {/* Quick Selector for Mobile */}
                <div className="lg:hidden">
                    <AIImageDropdownSizes 
                        model={imageModel}
                        size={imageSize}
                        setSize={updateCompanyImageSize}
                        className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                        parentClassName="w-full"
                        placement={PLACEMENT.BOTTOM_START}
                    />
                </div>

                {/* Aspect Ratio Cards for Desktop */}
                <div className="hidden lg:block">
                    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(availableSizeOptions.length, 7)}, minmax(0, 1fr))` }}>
                        {availableSizeOptions.map((sizeOption) => (
                            <AspectRatioCard
                                key={sizeOption.id}
                                sizeOption={sizeOption}
                                isSelected={imageSize === sizeOption.id}
                                onSelect={updateCompanyImageSize}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* AI Image Styles Section */}
            {!!aiImageStyles?.length && (
                <div className="space-y-6">
                    <FormDesription
                        title={
                            <span className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <HiColorSwatch className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                AI Image Styles
                            </span>
                        }
                        desc={
                            <span className="text-gray-600 dark:text-gray-300">
                                Select artistic styles that will be applied to your AI-generated images. Choose multiple styles to give variety to your content.
                            </span>
                        }
                    />
                    
                    {/* Style Selection Controls */}
                    <Card className="bg-gray-50 dark:bg-gray-800">
                        <div className="p-0">
                            <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <label 
                                        className='flex items-center cursor-pointer' 
                                        htmlFor='selectall'
                                    >
                                        <Checkbox
                                            id="selectall"
                                            className="mb-0 mr-2"
                                            checked={selectedStyles.length >= aiImageStyles.length}
                                            onChange={(value) => value 
                                                ? setSelectedStyles(aiImageStyles.map(e => e.style)) 
                                                : setSelectedStyles([])
                                            }
                                        />
                                        <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>Select All Styles</span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <HiSparkles className="w-4 h-4 text-purple-500" />
                                        <span className='text-sm text-gray-600 dark:text-gray-400'>
                                            {selectedStyles.length} of {aiImageStyles.length} styles selected
                                        </span>
                                    </div>
                                </div>
                                <Button 
                                    variant="solid"
                                    icon={<LuSave />}
                                    onClick={() => handleOnSave()}
                                    loading={isSubmitting}
                                    className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                                >
                                    Save Styles
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Styles Grid */}
                    <ResponsiveMasonry className="w-full" columnsCountBreakPoints={{ 350: 2, 475: 2, 750: 3, 1200: 4 }}>
                        <Masonry gutter='1rem'>
                            {aiImageStyles.map((item, key) => (
                                <div key={item.style} onClick={() => handleOnClick(item?.style)} className={`cursor-pointer flex flex-col gap-2 items-center justify-center group`}>
                                    <div className={`relative overflow-hidden rounded-2xl transition-all duration-200 shadow-sm hover:shadow-xl transform hover:scale-105 ${!!selectedStyles.find(e => e == item?.style) ? 'ring-4 ring-purple-500 ring-opacity-50' : 'hover:shadow-lg'}`}>
                                        <img 
                                            src={item.image}
                                            className="w-full transition-all duration-200 group-hover:brightness-110 aspect-square object-cover"
                                            alt={item.style}
                                        />
                                        <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-200 ${!!selectedStyles.find(e => e == item?.style) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}></div>
                                        <Checkbox
                                            className="mb-0 absolute left-3 top-3 pointer-events-none z-10"
                                            checked={!!selectedStyles.find(e => e == item?.style)}
                                        />
                                        {!!selectedStyles.find(e => e == item?.style) && (
                                            <div className="absolute bottom-3 left-3 right-3">
                                                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-2 py-1">
                                                    <span className="text-xs font-medium text-gray-900 dark:text-white">Selected</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-center space-y-0">
                                        <Tooltip title={item?.description}>
                                            <h6 className="font-semibold text-gray-900 dark:text-white text-xs whitespace-nowrap w-full overflow-hidden text-ellipsis leading-tight">{item?.style}</h6>
                                        </Tooltip>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-2">{item?.description}</p>
                                    </div>
                                </div>
                            ))}
                        </Masonry>
                    </ResponsiveMasonry>
                </div>
            )}

            {/* Footer Note */}
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-1">
                    <HiInformationCircle className="w-4 h-4" />
                    <span className="font-medium">Settings Auto-Save</span>
                </div>
                <p>Your AI image preferences are automatically saved and will be applied to all future image generations.</p>
            </div>
        </div>
    )
}

export default AIImages
